import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, deleteDoc, Timestamp, type DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Trash2, Settings2, CalendarPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AvailabilityScheduler } from './AvailabilityScheduler';
import { activeDoctors, type DoctorOption } from '@/lib/scheduling/doctors';
import { triggerScheduling } from '@/lib/scheduling';
import { useLanguage } from '@/contexts/LanguageContext';
import { ScheduleSettingsDialog } from '@/components/admin/schedules/ScheduleSettingsDialog';

const getDays = (t: (key: string) => string) => ({
  0: t('dashboard.admin.availability.days.sunday'),
  1: t('dashboard.admin.availability.days.monday'),
  2: t('dashboard.admin.availability.days.tuesday'),
  3: t('dashboard.admin.availability.days.wednesday'),
  4: t('dashboard.admin.availability.days.thursday'),
  5: t('dashboard.admin.availability.days.friday'),
  6: t('dashboard.admin.availability.days.saturday')
});

const getSessions = (t: (key: string) => string) => ({
  'session1': t('dashboard.admin.availability.sessions.session1'),
  'session2': t('dashboard.admin.availability.sessions.session2')
});

export function AvailabilityList() {
  const [availabilities, setAvailabilities] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showScheduleSettings, setShowScheduleSettings] = useState(false);
  const [addForCoachOpen, setAddForCoachOpen] = useState(false);
  const [forCoachId, setForCoachId] = useState('');
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const { t, dir, language } = useLanguage();
  const isRTL = language === 'ar';
  const dateLocale = language === 'ar' ? ar : enUS;

  const getAvailabilityStatus = (availability: DocumentData) => {
    const now = new Date();
    const endDate = availability.endDate.toDate();
    
    if (availability.status === 'pending') {
      return 'pending';
    } else if (availability.status === 'approved' && endDate > now) {
      return 'active';
    } else {
      return 'expired';
    }
  };

  const fetchAvailabilities = async () => {
    try {
      // Fetch availabilities and all users in parallel
      const [availabilitiesSnapshot, usersSnapshot] = await Promise.all([
        getDocs(query(
          collection(db, 'availabilities'),
          where('status', 'in', ['pending', 'approved', 'rejected'])
        )),
        getDocs(collection(db, 'users'))
      ]);
      
      // Create a map of users for efficient lookup
      const usersMap = new Map();
      usersSnapshot.docs.forEach(doc => {
        usersMap.set(doc.id, doc.data());
      });
      
      // Process availabilities with coach data
      const availabilitiesData = availabilitiesSnapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data();
        const coachData = data.coachId ? usersMap.get(data.coachId) || {} : {};
        
        return {
          id: docSnapshot.id,
          ...data,
          coach: coachData
        };
      });
      
      setAvailabilities(availabilitiesData);
      setDoctors(activeDoctors(usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }))));
    } catch (error) {
      console.error('Error fetching availabilities:', error);
      toast.error(t('dashboard.admin.availability.actions.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailabilities();
  }, []);

  const handleApprove = async (availabilityId: string) => {
    try {
      setProcessingId(availabilityId);
      
      // First update the availability status
      await updateDoc(doc(db, 'availabilities', availabilityId), {
        status: 'approved',
        updatedAt: Timestamp.now()
      });

      toast.success(t('dashboard.admin.availability.actions.approveSuccess'));
      
      // Then trigger scheduling
      await triggerScheduling();
      
      await fetchAvailabilities();
    } catch (error) {
      console.error('Error approving availability:', error);
      toast.error(t('dashboard.admin.availability.actions.approveError'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (availabilityId: string) => {
    try {
      setProcessingId(availabilityId);
      await updateDoc(doc(db, 'availabilities', availabilityId), {
        status: 'rejected',
        updatedAt: Timestamp.now()
      });
      
      toast.success(t('dashboard.admin.availability.actions.rejectSuccess'));
      await fetchAvailabilities();
    } catch (error) {
      console.error('Error rejecting availability:', error);
      toast.error(t('dashboard.admin.availability.actions.rejectError'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (availabilityId: string) => {
    try {
      setProcessingId(availabilityId);
      await deleteDoc(doc(db, 'availabilities', availabilityId));
      
      toast.success(t('dashboard.admin.availability.actions.deleteSuccess'));
      await fetchAvailabilities();
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error(t('dashboard.admin.availability.actions.deleteError'));
    } finally {
      setProcessingId(null);
    }
  };

  const renderDaySchedule = (selectedDays: { [key: string]: string[] }) => {
    if (!selectedDays) return null;

    const DAYS = getDays(t);
    const SESSIONS = getSessions(t);

    return Object.entries(selectedDays).map(([day, sessions]) => (
      <div key={day} className="flex flex-col gap-0.5 md:gap-1 p-1.5 md:p-2 bg-secondary/50 rounded-lg">
        <span className="font-medium text-xs md:text-sm">{DAYS[day as keyof typeof DAYS]}</span>
        <div className="flex flex-wrap gap-0.5 md:gap-1">
          {sessions.map((session, index) => (
            <Badge key={index} variant="outline" className="text-[10px] md:text-xs px-1.5 md:px-2 py-0 md:py-0.5">
              {SESSIONS[session as keyof typeof SESSIONS]}
            </Badge>
          ))}
        </div>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" dir={dir}>
      {/* Header with Settings Button */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.tabs.availability') || (isRTL ? 'الجداول' : 'Schedules')}
          </h2>
          <p className="text-muted-foreground">
            {isRTL ? 'إدارة جداول توفر المدربين' : 'Manage coach availability schedules'}
          </p>
        </div>
        <div className="flex gap-2">
        <Button onClick={() => setAddForCoachOpen(true)} data-testid="add-schedule-for-doctor">
          <CalendarPlus className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
          {isRTL ? 'إضافة جدول لطبيب' : 'Add a schedule for a doctor'}
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowScheduleSettings(true)}
          className={`${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
        >
          <Settings2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
          {isRTL ? 'إعدادات الجداول' : 'Schedules Settings'}
        </Button>
        </div>
      </div>

      {/* An admin adds a schedule on a doctor's behalf, approved at once (#42) */}
      <Dialog
        open={addForCoachOpen}
        onOpenChange={(open) => {
          setAddForCoachOpen(open);
          if (!open) setForCoachId('');
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'إضافة جدول لطبيب' : 'Add a schedule for a doctor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={forCoachId} onValueChange={setForCoachId}>
              <SelectTrigger data-testid="schedule-doctor-select">
                <SelectValue placeholder={isRTL ? 'اختر الطبيب' : 'Choose the doctor'} />
              </SelectTrigger>
              <SelectContent>
                {doctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {isRTL ? d.nameAr : d.nameEn}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {isRTL
                ? 'يُحفظ الجدول باسم الطبيب ومعتمداً مباشرة.'
                : "Saved under the doctor's name and approved at once."}
            </p>
            {forCoachId && (
              <AvailabilityScheduler
                key={forCoachId}
                forCoach={{
                  id: forCoachId,
                  name: (() => {
                    const d = doctors.find((x) => x.id === forCoachId);
                    return d ? d.nameEn || d.nameAr : '';
                  })(),
                }}
                onCancel={() => setAddForCoachOpen(false)}
                onSubmitSuccess={async () => {
                  setAddForCoachOpen(false);
                  setForCoachId('');
                  await triggerScheduling();
                  await fetchAvailabilities();
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {availabilities.map((availability) => {
        const status = getAvailabilityStatus(availability);
        return (
        <div
          key={availability.id}
          className="rounded-lg border bg-card text-card-foreground shadow-sm p-3 md:p-4 lg:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 md:gap-3 mb-3 md:mb-4">
            <div className="flex-1 min-w-0">
              <div className="mb-1 md:mb-2">
                <h3 className="text-base md:text-lg font-semibold text-primary truncate">
                  {availability.coach ? 
                    language === 'ar' 
                      ? `المدرب ${availability.coach.firstNameAr || availability.coach.firstNameEn || availability.coach.firstName || ''} ${availability.coach.lastNameAr || availability.coach.lastNameEn || availability.coach.lastName || ''}`.trim() || 'مدرب غير معروف'
                      : `Coach ${availability.coach.firstNameEn || availability.coach.firstName || ''} ${availability.coach.lastNameEn || availability.coach.lastName || ''}`.trim() || 'Unknown Coach'
                    : language === 'ar' ? 'مدرب غير معروف' : 'Unknown Coach'
                  }
                </h3>
              </div>
              <p className="text-xs md:text-sm text-muted-foreground">
                {format(availability.startDate.toDate(), 'PPP', { locale: dateLocale })} - {format(availability.endDate.toDate(), 'PPP', { locale: dateLocale })}
              </p>
            </div>
            <Badge 
              variant="secondary" 
              className={`self-start text-xs ${
                status === 'pending' ? 'bg-orange-100 text-orange-800' :
                status === 'active' ? '' :
                'bg-gray-100 text-gray-600'
              }`}
            >
              {t(`dashboard.coach.availability.status.${status}`)}
            </Badge>
          </div>
          
          <div className="grid gap-1.5 md:gap-2 mb-3 md:mb-4">
            {renderDaySchedule(availability.selectedDays)}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 md:gap-2">
            {status === 'pending' && (
              <>
                <Button
                  onClick={() => handleApprove(availability.id)}
                  disabled={!!processingId}
                  className="bg-[#7dda55] hover:bg-[#6bc947] text-white text-xs md:text-sm h-8 md:h-9 w-full sm:w-auto"
                  size="sm"
                >
                  {processingId === availability.id ? (
                    <Loader2 className={`h-3.5 w-3.5 md:h-4 md:w-4 animate-spin ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                  ) : (
                    <CheckCircle className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                  )}
                  {t('dashboard.admin.availability.actions.approve')}
                </Button>
                <Button
                  onClick={() => handleReject(availability.id)}
                  disabled={!!processingId}
                  variant="destructive"
                  className="text-xs md:text-sm h-8 md:h-9 w-full sm:w-auto"
                  size="sm"
                >
                  {processingId === availability.id ? (
                    <Loader2 className={`h-3.5 w-3.5 md:h-4 md:w-4 animate-spin ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                  ) : (
                    <XCircle className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                  )}
                  {t('dashboard.admin.availability.actions.reject')}
                </Button>
              </>
            )}
            {(status === 'active' || status === 'expired') && (
              <Button
                onClick={() => handleDelete(availability.id)}
                disabled={!!processingId}
                variant="destructive"
                className="text-xs md:text-sm h-8 md:h-9 w-full sm:w-auto"
                size="sm"
              >
                {processingId === availability.id ? (
                  <Loader2 className={`h-3.5 w-3.5 md:h-4 md:w-4 animate-spin ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                ) : (
                  <Trash2 className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                )}
                {t('dashboard.admin.availability.actions.delete')}
              </Button>
            )}
          </div>
        </div>
      )})}
      
      {availabilities.length === 0 && (
        <div className="text-center py-6 md:py-8 text-xs md:text-sm text-muted-foreground">
          {t('dashboard.admin.availability.noAvailabilities')}
        </div>
      )}

      {/* Schedule Settings Dialog */}
      <ScheduleSettingsDialog 
        open={showScheduleSettings} 
        onOpenChange={setShowScheduleSettings} 
      />
    </div>
  );
}